/**
 * #1684: what a cancelled job leaves behind in Purchasing. POST /jobs/:id/cancel
 * returns the job's still-open purchase orders; this turns them into the one
 * sentence the office needs — which vendor already has an order to call off.
 * Pure, so it is unit-tested directly; the callers show it as a toast.
 */

export interface CancelledJobPo {
    id: string;
    po_number: string | null;
    status: string;
    vendor_name: string | null;
    /** False only for draft / needs_review — the vendor has never seen it. */
    sent_to_vendor: boolean;
}

export interface CancelledJobPoNotice {
    message: string;
    /** The PO the toast's button opens: the first one a vendor already has, else the first draft. */
    poId: string;
}

const label = (po: CancelledJobPo) => po.po_number ?? "unnumbered PO";
const who = (po: CancelledJobPo) => po.vendor_name ?? "the vendor";
const capitalize = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export function cancelledJobPoNotice(pos: CancelledJobPo[] | null | undefined): CancelledJobPoNotice | null {
    if (!pos || pos.length === 0) return null;
    const sent = pos.filter((p) => p.sent_to_vendor);
    const drafts = pos.filter((p) => !p.sent_to_vendor);

    if (sent.length > 0) {
        const list = sent.map((p) => `${label(p)} (${who(p)})`).join(", ");
        const lead =
            sent.length === 1
                ? `${capitalize(who(sent[0]))} already has ${sent[0].po_number ? `order ${sent[0].po_number}` : "an order"} for this job.`
                : `${sent.length} orders for this job already went to vendors: ${list}.`;
        const draftNote =
            drafts.length > 0
                ? ` ${drafts.length} unsent draft${drafts.length === 1 ? "" : "s"} can be cancelled in Purchasing too.`
                : "";
        return {
            message:
                sent.length === 1
                    ? `${lead} Call to cancel it, then cancel the PO in Purchasing.${draftNote}`
                    : `${lead} Call to cancel them, then cancel the POs in Purchasing.${draftNote}`,
            poId: sent[0].id,
        };
    }

    return {
        message:
            drafts.length === 1
                ? `This job still has an unsent PO${drafts[0].po_number ? ` (${drafts[0].po_number})` : ""}. Cancel it in Purchasing.`
                : `This job still has ${drafts.length} unsent POs. Cancel them in Purchasing.`,
        poId: drafts[0].id,
    };
}
